import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import Brand from "../../components/Brand";

/* /dashboard/goli — Supplements HUB inside the dashboard.

   Mike's hard rule: NO icon-only cards. Every product card must lead with
   a LARGE real photo of a person using the product (or the actual product
   in real life). Lifestyle first, copy second.

   Two wellness partners — Goli + Piurify — side-by-side, equal weight,
   each card image-dominant. */

const G    = "#2E9F3A";
const O    = "#F39A1F";
const WINE = "#0F172A";

/* Real lifestyle photos — all of these are either customer-uploaded by
   Mike or Pexels CC0 stock of real people. NO AI illustrations. */
const PARTNERS = [
  {
    key: "goli",
    name: "Goli",
    tag: "Apple-Cider Vinegar Gummies",
    badge: "MIKE'S DAILY PICK",
    badgeColor: G,
    accent: G,
    image: "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/gs6eb9by_Screenshot%202026-05-13%20221254.png",
    alt: "Real Goli customer smiling, holding her bottle of apple-cider gummies",
    blurb: "Easy-to-swallow gummies. Earn 20% commission on every sale. Best for first-time supplement audiences.",
    to: "/goli",
    cta: "See Goli Page",
    external: false,
  },
  // Brainzyme removed Feb 2026 (Mike's call).
  {
    key: "piurify",
    name: "Piurify",
    tag: "Hydrogen Water Bottle",
    badge: "HEALTH UPGRADE",
    badgeColor: "#287F8E",
    accent: "#287F8E",
    image: "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/5e4dmb78_Screenshot%202026-05-13%20134341.png",
    alt: "Blond woman customer enjoying her Piurify hydrogen water bottle — real review",
    blurb: "Portable hydrogen-water generator. retail price · 60-day returns · lifetime warranty · free shipping. Mike earns 10% per sale.",
    to: "/piurify",
    cta: "See Piurify Page",
    external: false,
  },
  // Nature's Brands removed Feb 2026 (Mike rotating Christian-aligned organic partners in).
  // LiveGood removed Feb 2026 (MLM concerns).
];

export default function SupplementsHubTab() {
  return (
    <div className="space-y-6" data-testid="supplements-hub-tab">
      {/* Tab header */}
      <div className="rounded-2xl p-5 md:p-6 shadow-md"
           style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #EAF6FC 100%)", border: `3px solid ${O}` }}>
        <div className="text-[11px] tracking-[0.3em] uppercase font-bold mb-1" style={{ color: O }}>
          ★ <Brand/> · WELLNESS PARTNERS ★
        </div>
        <h2 className="font-serif-display text-3xl md:text-4xl uppercase leading-tight" style={{ color: WINE }}>
          Supplements &amp; Wellness
        </h2>
        <p className="text-base md:text-lg text-foreground/85 mt-2 max-w-2xl">
          Five vetted partners. Tap any card to see the full sales page — real people, real products, real commissions when you share your link.
        </p>
      </div>

      {/* 4 image-dominant partner cards */}
      <div className="grid sm:grid-cols-2 gap-5" data-testid="supplements-partners-grid">
        {PARTNERS.map((p) => {
          const inner = (
            <>
              {/* Big lifestyle photo — top 60% of the card */}
              <div className="relative aspect-[5/4] overflow-hidden bg-white">
                <img
                  src={p.image}
                  alt={p.alt}
                  className="block w-full h-full object-contain p-3 bg-white group-hover:scale-[1.04] transition-transform duration-500"
                  loading="lazy"
                />
                <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                      style={{ background: p.badgeColor }}>
                  ★ {p.badge}
                </span>
              </div>

              {/* Copy area */}
              <div className="p-5 flex flex-col flex-1">
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: p.accent }}>
                  {p.tag}
                </div>
                <div className="font-serif-display text-3xl md:text-4xl mt-1 leading-tight" style={{ color: WINE, fontWeight: 700 }}>
                  {p.name}
                </div>
                <p className="text-base text-foreground/85 mt-2 leading-relaxed flex-1">
                  {p.blurb}
                </p>
                <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition self-start"
                     style={{ background: p.accent }}>
                  {p.cta} {p.external ? <ExternalLink className="w-4 h-4"/> : <ArrowRight className="w-4 h-4"/>}
                </div>
              </div>
            </>
          );

          return p.external ? (
            <a key={p.key} href={p.to} target="_blank" rel="noopener noreferrer"
               data-testid={`supp-pick-${p.key}`}
               className="group rounded-2xl bg-white border-2 overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition flex flex-col"
               style={{ borderColor: `${p.accent}40` }}>
              {inner}
            </a>
          ) : (
            <Link key={p.key} to={p.to}
                  data-testid={`supp-pick-${p.key}`}
                  className="group rounded-2xl bg-white border-2 overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition flex flex-col"
                  style={{ borderColor: `${p.accent}40` }}>
              {inner}
            </Link>
          );
        })}
      </div>

      {/* Affiliate note */}
      <div className="rounded-2xl p-5 text-center" style={{ background: "#EAF6FC", border: `2px solid ${O}55` }} data-testid="supplements-affiliate-note">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-1" style={{ color: O }}>★ HOW IT WORKS</div>
        <p className="text-base text-foreground/85 leading-relaxed max-w-2xl mx-auto">
          Click any partner above to see the full sales page. Each partner pays Mike a real commission on every sale that comes through these links — and you can share these pages with your own audience for affiliate income.
        </p>
      </div>
    </div>
  );
}
